const { jQuery: $ } = window;

import { getBaseUrl } from "./url";

/**
 * Builds the numbered pagination UI for a shortcode. Hook surface: pagination_range, pagination_settings,
 * pagination_button, pagination_separator; emits the `data-page` / `/page/N/` anchors the click handler uses.
 */
export default class Pagination {
    /** @member {Hooks} */
    #hooks;

    /** @member {Logger} */
    #logger;

    /**
     * @param {Hooks} hooks
     * @param {Logger} logger
     */
    constructor(hooks, logger) {
        this.#hooks = hooks;
        this.#logger = logger;
    }

    /**
     * Renders pagination into the instance's `.kpfsc__paginationNumbers` container. When pagination is
     * disabled the shortcode gets `kpfsc--no-pages`.
     *
     * @param {ShortcodeInstance} instance
     */
    load(instance) {
        const $shortcode = instance.$el;
        const store = instance.store;

        $shortcode.find(".kpfsc__paginationNumbers").each((i, el) => {
            const $el = $(el);
            const current = store.get("filterPage");
            const meta = store.get("queryMeta");
            const range = this.#hooks.applyFilter("pagination_range", 2);

            if(store.get("pagination")) {
                this.#logger.start("Pagination");

                let pagination = {
                    current: {page: current, title: "Current Page", text: current, class: "kpfsc__pageBtn--current kpfsc__pageBtn--number"},
                    first: (current > 1 + range ? {page: 1, title: "First Page", text: `<span class='kpfsc__btnMarker' aria-hidden='true'>&laquo;</span><span class='kpfsc__btnLabel'>1</span>`, class: "kpfsc__pageBtn--first"} : null),
                    prev: (current > 1 ? {page: current - 1, title: "Previous Page", text: "<span class='kpfsc__btnMarker' aria-hidden='true'>&lsaquo;</span><span class='kpfsc__btnLabel'>Back</span>", class: "kpfsc__pageBtn--prev"} : null),
                    last: (current < meta.pages - range ? {page: meta.pages, title: "Last Page", text: `<span class='kpfsc__btnLabel'>${meta.pages}</span><span class='kpfsc__btnMarker' aria-hidden='true'>&raquo;</span>`, class: "kpfsc__pageBtn--last"} : null),
                    next: (current < meta.pages ? {page: current + 1 , title: "Next Page", text: "<span class='kpfsc__btnLabel'>Next</span><span class='kpfsc__btnMarker' aria-hidden='true'>&rsaquo;</span>", class: "kpfsc__pageBtn--next"} : null),
                    separators: true,
                    numbers: []
                };

                for(let i = current - range; i <= current + range; i++) {
                    if(i >= 1 && i <= meta.pages) {
                        pagination.numbers.push({page: i, title: `Page #${i}`, text: i, class: "kpfsc__pageBtn--number"});
                    }
                }

                pagination = this.#hooks.applyFilter("pagination_settings", pagination, $shortcode);

                this.#logger.log("pagination", pagination);

                $el.html(""); // Clear out any existing pagination

                if(pagination.numbers.length === 1) {
                    this.#logger.end();
                    return;
                }

                const baseURL = getBaseUrl();

                if(pagination.prev) {
                    $el.append(this.#hooks.applyFilter("pagination_button",
                        `<a href="${baseURL}/page/${pagination.prev.page}/${location.search}${location.hash}" class="kpfsc__pageBtn ${pagination.prev.class}" data-page="${pagination.prev.page}" title="${pagination.prev.title}" aria-label="${pagination.prev.title}">${pagination.prev.text}</a>`,
                        pagination.prev, pagination, $shortcode
                    ));
                }

                if(pagination.first) {
                    $el.append(this.#hooks.applyFilter("pagination_button",
                        `<a href="${baseURL}/page/${pagination.first.page}/${location.search}${location.hash}" class="kpfsc__pageBtn ${pagination.first.class}" data-page="${pagination.first.page}" title="${pagination.first.title}" aria-label="${pagination.first.title}">${pagination.first.text}</a>`,
                        pagination.first, pagination, $shortcode
                    ));
                }

                if(pagination.separators && pagination.first && current > range + 2) {
                    $el.append(this.#hooks.applyFilter("pagination_separator",
                        `<span class="kpfsc__paginationSeparator kpfsc__paginationSeparator--left">&hellip;</span>`,
                        pagination, $shortcode
                    ));
                }

                for(let data of pagination.numbers) {
                    if(data.page === pagination.current.page) {
                        data = pagination.current;
                    }

                    $el.append(this.#hooks.applyFilter("pagination_button",
                        `<a href="${baseURL}/page/${data.page}/${location.search}${location.hash}" class="kpfsc__pageBtn ${data.class}" data-page="${data.page}" title="${data.title}" aria-label="${data.title}"${data.class.includes("--current") ? ' aria-current="page"' : ""}>${data.text}</a>`,
                        data, pagination, $shortcode
                    ));
                }

                if(pagination.separators && pagination.last && current < pagination.last.page - range - 1) {
                    $el.append(this.#hooks.applyFilter("pagination_separator",
                        `<span class="kpfsc__paginationSeparator kpfsc__paginationSeparator--right">&hellip;</span>`,
                        pagination, $shortcode
                    ));
                }

                if(pagination.last) {
                    $el.append(this.#hooks.applyFilter("pagination_button",
                        `<a href="${baseURL}/page/${pagination.last.page}/${location.search}${location.hash}" class="kpfsc__pageBtn ${pagination.last.class}" data-page="${pagination.last.page}" title="${pagination.last.title}" aria-label="${pagination.last.title}">${pagination.last.text}</a>`,
                        pagination.last, pagination, $shortcode
                    ));
                }

                if(pagination.next) {
                    $el.append(this.#hooks.applyFilter("pagination_button",
                        `<a href="${baseURL}/page/${pagination.next.page}/${location.search}${location.hash}" class="kpfsc__pageBtn ${pagination.next.class}" data-page="${pagination.next.page}" title="${pagination.next.title}" aria-label="${pagination.next.title}">${pagination.next.text}</a>`,
                        pagination.next, pagination, $shortcode
                    ));
                }

                this.#logger.end();
            } else {
                $shortcode.addClass("kpfsc--no-pages");
            }
        });
    }
}
